import { Button } from "@/components/Button/Button";
import { PageWithScene } from "@/components/PageWithScene/PageWithScene";
import { env } from "@/lib/env";
import { useCallback, useId, useState } from "react";
import { ButtonGroup } from "../ButtonGroup/ButtonGroup";
import css from "./RouteError.module.scss";

const RESET_ERROR_TIMEOUT_MS = 200;

type Props = {
  error: unknown;
  stacktrace?: string | null;
  onReset: () => void;
};

export function RouteError({ error, stacktrace, onReset }: Props) {
  const [isResetting, setIsResetting] = useState(false);
  const [showStacktrace, setShowStacktrace] = useState(false);
  const stacktraceId = useId();

  // Reset error after a short timeout, just so we can show a spinner in the
  // reset button so users feel like they clicked the button.
  const resetErrorAfterTimeout = useCallback((resetError: () => void) => {
    setIsResetting(true);

    setTimeout(() => {
      resetError();
      setIsResetting(false);
    }, RESET_ERROR_TIMEOUT_MS);
  }, []);

  function handleToggleShowStacktrace() {
    setShowStacktrace(!showStacktrace);
  }

  return (
    <div className="l-wrapper">
      <PageWithScene heading="Noe gikk galt" scene="Mallory-sneaking-Bob">
        <ButtonGroup>
          <Button onClick={() => resetErrorAfterTimeout(onReset)} variant="primary" active={isResetting}>
            Prøv igjen
          </Button>

          {env.RUN_ENV !== "production" && (
            <Button
              onClick={handleToggleShowStacktrace}
              variant="secondary"
              aria-controls={stacktraceId}
              aria-pressed={showStacktrace}
            >
              Vis stacktrace
            </Button>
          )}
        </ButtonGroup>

        {env.RUN_ENV !== "production" && (
          <div id={stacktraceId} className={css.componentStackContainer}>
            {showStacktrace && (
              <pre className={css.componentStack} data-visible={showStacktrace}>
                {error instanceof Error && `${error.name}: ${error.message}`}
                {stacktrace}
              </pre>
            )}
          </div>
        )}
      </PageWithScene>
    </div>
  );
}
